/**
 * The browser half of a passkey ceremony.
 *
 * The API hands out WebAuthn options as JSON, with every binary field in base64url, and expects the
 * credential back the same way. Recent browsers convert both directions themselves
 * (`PublicKeyCredential.parseCreationOptionsFromJSON`, `credential.toJSON()`); the Safari and Android
 * WebViews parents actually use do not all have them yet, so each direction falls back to doing it here.
 *
 * Nothing in this file talks to the API. The client fetches the options and posts the result; this turns the
 * one into the other through the authenticator.
 */

/** Why a ceremony did not produce a credential, in the terms a screen words its message by. */
export type PasskeyFailure =
  /** The person closed the prompt, or let it time out. Nothing to report as an error. */
  | 'cancelled'
  /** Registering a passkey this authenticator already holds for the account. */
  | 'already-registered'
  /** This browser has no WebAuthn at all. */
  | 'unsupported'
  /** Anything else the authenticator refused. */
  | 'failed';

export class PasskeyError extends Error {
  readonly reason: PasskeyFailure;

  constructor(reason: PasskeyFailure, cause?: unknown) {
    super(`Passkey ceremony ${reason}.`, { cause });
    this.name = 'PasskeyError';
    this.reason = reason;
  }
}

/** Whether this browser can hold and use passkeys at all. Screens hide the passkey controls when it cannot. */
export function passkeysSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof window.PublicKeyCredential === 'function'
    && typeof navigator !== 'undefined'
    && typeof navigator.credentials?.create === 'function'
    && typeof navigator.credentials?.get === 'function';
}

// ---- base64url --------------------------------------------------------------------------------------------

export function toBase64Url(bytes: ArrayBuffer | ArrayBufferView): string {
  const view = bytes instanceof ArrayBuffer
    ? new Uint8Array(bytes)
    : new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let binary = '';
  for (const byte of view) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64Url(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

// ---- Options in -------------------------------------------------------------------------------------------

/** A credential descriptor as the API writes it. */
interface DescriptorJson {
  id: string;
  type: string;
  transports?: string[];
}

/** Creation options as the API writes them: `PublicKeyCredentialCreationOptionsJSON`. */
export interface CreationOptionsJson {
  challenge: string;
  user: { id: string; name: string; displayName: string };
  excludeCredentials?: DescriptorJson[];
  [key: string]: unknown;
}

/** Request options as the API writes them: `PublicKeyCredentialRequestOptionsJSON`. */
export interface RequestOptionsJson {
  challenge: string;
  allowCredentials?: DescriptorJson[];
  [key: string]: unknown;
}

/** The statics recent browsers add, which not every TypeScript `lib.dom` declares yet. */
interface NativeJsonSupport {
  parseCreationOptionsFromJSON?: (json: unknown) => PublicKeyCredentialCreationOptions;
  parseRequestOptionsFromJSON?: (json: unknown) => PublicKeyCredentialRequestOptions;
}

const native = (): NativeJsonSupport =>
  (typeof window !== 'undefined' ? window.PublicKeyCredential : undefined) as unknown as NativeJsonSupport ?? {};

/** The API may wrap the options as `{ publicKey: … }`, as `navigator.credentials` itself takes them. */
function unwrap<T>(json: T | { publicKey: T }): T {
  return json && typeof json === 'object' && 'publicKey' in json ? (json as { publicKey: T }).publicKey : json as T;
}

const descriptor = (d: DescriptorJson): PublicKeyCredentialDescriptor => ({
  type: d.type as PublicKeyCredentialType,
  id: fromBase64Url(d.id),
  ...(d.transports ? { transports: d.transports as AuthenticatorTransport[] } : {}),
});

export function toCreationOptions(json: CreationOptionsJson | { publicKey: CreationOptionsJson }): PublicKeyCredentialCreationOptions {
  const options = unwrap(json);
  const parse = native().parseCreationOptionsFromJSON;
  if (parse) return parse(options);

  return {
    ...(options as unknown as PublicKeyCredentialCreationOptions),
    challenge: fromBase64Url(options.challenge),
    user: { ...options.user, id: fromBase64Url(options.user.id) },
    ...(options.excludeCredentials ? { excludeCredentials: options.excludeCredentials.map(descriptor) } : {}),
  };
}

export function toRequestOptions(json: RequestOptionsJson | { publicKey: RequestOptionsJson }): PublicKeyCredentialRequestOptions {
  const options = unwrap(json);
  const parse = native().parseRequestOptionsFromJSON;
  if (parse) return parse(options);

  return {
    ...(options as unknown as PublicKeyCredentialRequestOptions),
    challenge: fromBase64Url(options.challenge),
    ...(options.allowCredentials ? { allowCredentials: options.allowCredentials.map(descriptor) } : {}),
  };
}

// ---- Credential out ---------------------------------------------------------------------------------------

/** The getters an attestation response has in browsers new enough to have them. */
interface AttestationExtras {
  getTransports?: () => string[];
  getAuthenticatorData?: () => ArrayBuffer;
  getPublicKey?: () => ArrayBuffer | null;
  getPublicKeyAlgorithm?: () => number;
}

/** The credential as the API reads it: `RegistrationResponseJSON` or `AuthenticationResponseJSON`. */
export function credentialToJson(credential: PublicKeyCredential): string {
  const withJson = credential as PublicKeyCredential & { toJSON?: () => unknown };
  if (typeof withJson.toJSON === 'function') return JSON.stringify(withJson.toJSON());

  const response = credential.response;
  let body: Record<string, unknown>;

  if ('attestationObject' in response) {
    const attestation = response as AuthenticatorAttestationResponse & AttestationExtras;
    const publicKey = attestation.getPublicKey?.();
    body = {
      clientDataJSON: toBase64Url(attestation.clientDataJSON),
      attestationObject: toBase64Url(attestation.attestationObject),
      ...(attestation.getTransports ? { transports: attestation.getTransports() } : {}),
      ...(attestation.getAuthenticatorData ? { authenticatorData: toBase64Url(attestation.getAuthenticatorData()) } : {}),
      ...(publicKey ? { publicKey: toBase64Url(publicKey) } : {}),
      ...(attestation.getPublicKeyAlgorithm ? { publicKeyAlgorithm: attestation.getPublicKeyAlgorithm() } : {}),
    };
  } else {
    const assertion = response as AuthenticatorAssertionResponse;
    body = {
      clientDataJSON: toBase64Url(assertion.clientDataJSON),
      authenticatorData: toBase64Url(assertion.authenticatorData),
      signature: toBase64Url(assertion.signature),
      ...(assertion.userHandle ? { userHandle: toBase64Url(assertion.userHandle) } : {}),
    };
  }

  return JSON.stringify({
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    response: body,
    ...(credential.authenticatorAttachment ? { authenticatorAttachment: credential.authenticatorAttachment } : {}),
    clientExtensionResults: credential.getClientExtensionResults(),
  });
}

// ---- Ceremonies -------------------------------------------------------------------------------------------

function failureOf(error: unknown): PasskeyFailure {
  if (error instanceof DOMException) {
    // NotAllowedError covers both "the person said no" and "the prompt timed out"; the browser will not say
    // which, on purpose. AbortError is the browser abandoning the prompt, as when another one replaces it.
    if (error.name === 'NotAllowedError' || error.name === 'AbortError') return 'cancelled';
    if (error.name === 'InvalidStateError') return 'already-registered';
  }
  return 'failed';
}

async function ceremony(run: () => Promise<Credential | null>): Promise<string> {
  if (!passkeysSupported()) throw new PasskeyError('unsupported');

  let credential: Credential | null;
  try {
    credential = await run();
  } catch (error) {
    throw new PasskeyError(failureOf(error), error);
  }

  if (!credential) throw new PasskeyError('cancelled');
  return credentialToJson(credential as PublicKeyCredential);
}

/** Creates a passkey for the options the API gave, and returns the credential to send back. */
export function createPasskey(options: CreationOptionsJson | { publicKey: CreationOptionsJson }): Promise<string> {
  return ceremony(() => navigator.credentials.create({ publicKey: toCreationOptions(options) }));
}

/** Signs the API's challenge with a passkey, and returns the assertion to send back. */
export function getPasskey(options: RequestOptionsJson | { publicKey: RequestOptionsJson }): Promise<string> {
  return ceremony(() => navigator.credentials.get({ publicKey: toRequestOptions(options) }));
}
